import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container } from './Container';
import { AppRoutes } from '../../types';
import { Button } from '../UI/Button';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useCart } from '../../context/CartContext';
import { useCartUI } from '../../context/CartUIContext';
import { usePageVisibility } from '../../hooks/usePageVisibility';
import { Currency } from '../../data/productModels';
import { LOCALES } from '../../i18n/locales';
import type { PageVisibilityKey } from '../../lib/pageVisibility';

const CURRENCY_OPTS: { code: Currency; symbol: string }[] = [
  { code: 'HKD', symbol: '$' },
  { code: 'USD', symbol: '$' },
  { code: 'GBP', symbol: '£' },
];

interface NavDropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  ariaLabel: string;
  className?: string;
  align?: 'left' | 'right';
}

const NavDropdown: React.FC<NavDropdownProps> = ({
  trigger,
  children,
  ariaLabel,
  className = '',
  align = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const toggle = useCallback(() => setIsOpen(open => !open), []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [close, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
        'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    });
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        ref={triggerRef}
        onClick={toggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-natuvia-text/5 transition-colors font-bold text-sm text-natuvia-text group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-offset-2 focus-visible:ring-offset-natuvia-primary"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {trigger}
        <span
          className={`text-[10px] opacity-50 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
          ▼
        </span>
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          className={`absolute top-full mt-2 w-48 bg-white border border-natuvia-text/10 shadow-xl rounded-lg overflow-hidden z-50 flex flex-col py-1 ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, content, makePath } = useLanguage();
  const { user, signOut } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const { totalQty } = useCart();
  const { openCart } = useCartUI();
  const { isVisible } = usePageVisibility();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  // Supabase URL Config - Using SVG
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const logoUrl = supabaseUrl
    ? `${supabaseUrl}/storage/v1/object/public/natuvia-assets/brand/logo.svg`
    : '/brand/logo.svg';

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) {
      lastFocusedElementRef.current?.focus();
      return;
    }

    lastFocusedElementRef.current = document.activeElement as HTMLElement | null;
    requestAnimationFrame(() => {
      mobileCloseButtonRef.current?.focus();
    });
  }, [isOpen]);

  const links: Array<{ key: PageVisibilityKey; label: string; path: AppRoutes }> = [
    { key: 'about', label: content.nav.about, path: AppRoutes.ABOUT },
    { key: 'products', label: content.nav.products, path: AppRoutes.PRODUCTS },
    { key: 'journal', label: content.nav.journal, path: AppRoutes.JOURNAL },
    { key: 'faq', label: content.nav.faq, path: AppRoutes.FAQ },
    { key: 'petServices', label: content.nav.petServices, path: AppRoutes.PET_SERVICES },
    { key: 'contact', label: content.nav.contact, path: AppRoutes.CONTACT },
  ];

  const handleCurrencyChange = (newCurrency: Currency) => setCurrency(newCurrency);
  const activeCurrencyData = CURRENCY_OPTS.find(c => c.code === currency) || CURRENCY_OPTS[0];
  const activeLangData = LOCALES[language as keyof typeof LOCALES] || LOCALES['en'];

  const ui = {
    en: {
      login: 'Login',
      account: 'Account',
      logout: 'Logout',
      currency: 'Currency',
      language: 'Language',
    },
    'zh-hk': {
      login: '登入',
      account: '會員中心',
      logout: '登出',
      currency: '幣別',
      language: '語言',
    },
  };
  const t = ui[language as keyof typeof ui] || ui['en'];

  const handleLogout = async () => {
    await signOut();
    navigate(makePath(AppRoutes.HOME));
  };

  const isActive = (path: string) => {
    const fullPath = makePath(path);
    return (
      location.pathname === fullPath || (location.pathname.startsWith(fullPath) && path !== '/')
    );
  };

  return (
    <>
      {/* Skip-to-content link: visible only when focused via keyboard. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-natuvia-cta focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-natuvia-cta/30"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-50 w-full bg-natuvia-primary/95 backdrop-blur-sm border-b border-natuvia-text/5">
        <Container>
          <div className="flex items-center justify-between h-20">
            <Link to={makePath(AppRoutes.HOME)} className="flex items-center gap-2 group py-2">
              {/* Image Logo with Fallback to Text */}
              <div className="flex items-center">
                <img
                  src={logoUrl}
                  alt="Natuvía"
                  width={86}
                  height={48}
                  decoding="async"
                  fetchPriority="high"
                  className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
                <span className="hidden text-2xl font-extrabold tracking-tight text-natuvia-text">
                  Natuvía
                </span>
              </div>
            </Link>

            <nav
              className="hidden md:flex items-center gap-4 lg:gap-6"
              aria-label="Main navigation"
            >
              {links
                .filter(link => isVisible(link.key))
                .map(link => (
                  <Link
                    key={link.path}
                    to={makePath(link.path)}
                    className={`text-sm font-bold tracking-wide transition-colors ${
                      isActive(link.path)
                        ? 'text-natuvia-highlight'
                        : 'text-natuvia-text hover:text-natuvia-highlight'
                    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-offset-2 focus-visible:ring-offset-natuvia-primary rounded-md`}
                  >
                    {link.label}
                  </Link>
                ))}

              <div className="h-4 w-px bg-natuvia-text/20 mx-2" />

              {/* Fixed: Use NavDropdown consistently */}
              <NavDropdown
                ariaLabel="Select currency"
                trigger={
                  <span>
                    {activeCurrencyData.symbol} {activeCurrencyData.code}
                  </span>
                }
              >
                {CURRENCY_OPTS.map(c => (
                  <button
                    key={c.code}
                    onClick={() => handleCurrencyChange(c.code)}
                    className={`flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-natuvia-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-inset ${currency === c.code ? 'text-natuvia-ctaDark font-bold' : 'text-natuvia-text'}`}
                  >
                    <span>{c.code}</span>
                    <span className="font-mono opacity-50">{c.symbol}</span>
                  </button>
                ))}
              </NavDropdown>

              {/* Fixed: Use NavDropdown consistently */}
              <NavDropdown
                ariaLabel="Select language"
                trigger={
                  <span>
                    {activeLangData.label.split(' ')[0]} {activeLangData.pathPrefix.toUpperCase()}
                  </span>
                }
              >
                {Object.entries(LOCALES).map(([key, data]) => (
                  <button
                    key={key}
                    onClick={() => setLanguage(key as any)}
                    className={`px-4 py-2.5 text-sm text-left transition-colors hover:bg-natuvia-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-inset ${language === key ? 'text-natuvia-ctaDark font-bold' : 'text-natuvia-text'}`}
                  >
                    {data.label}
                  </button>
                ))}
              </NavDropdown>

              {user ? (
                <div className="flex gap-4 items-center">
                  <Link
                    to={makePath(AppRoutes.ACCOUNT)}
                    className="text-sm font-bold text-natuvia-text hover:text-natuvia-highlight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-offset-2 focus-visible:ring-offset-natuvia-primary rounded-md"
                  >
                    {t.account}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-sm font-bold text-natuvia-text/60 hover:text-natuvia-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-offset-2 focus-visible:ring-offset-natuvia-primary rounded-md"
                  >
                    {t.logout}
                  </button>
                </div>
              ) : (
                <Link
                  to={makePath(AppRoutes.LOGIN)}
                  className="text-sm font-bold text-natuvia-text hover:text-natuvia-highlight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-offset-2 focus-visible:ring-offset-natuvia-primary rounded-md"
                >
                  {t.login}
                </Link>
              )}

              <button
                onClick={openCart}
                className="relative p-3 min-w-[44px] min-h-[44px] rounded-lg hover:bg-natuvia-text/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-offset-2 focus-visible:ring-offset-natuvia-primary"
                aria-label="Open cart"
              >
                <svg
                  className="w-6 h-6 text-natuvia-text"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <AnimatePresence>
                  {totalQty > 0 && (
                    <motion.span
                      key={totalQty}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                      className="absolute -top-1 -right-1 bg-natuvia-ctaDark text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      {totalQty > 99 ? '99+' : totalQty}
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>

              <Button
                variant="primary"
                to={makePath(AppRoutes.SHOP)}
                className="ml-2 !py-2 !px-4 text-sm"
              >
                {content.nav.shop}
              </Button>
            </nav>

            <button
              className="md:hidden p-3 min-w-[44px] min-h-[44px] text-natuvia-text"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              ref={mobileMenuButtonRef}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {isOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </Container>
      </header>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl z-50 md:hidden overflow-y-auto"
              id="mobile-menu"
              ref={mobileMenuRef}
              role="dialog"
              aria-modal="true"
              aria-label="Mobile menu"
              onKeyDown={event => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setIsOpen(false);
                  return;
                }

                if (event.key !== 'Tab') return;

                const focusables = mobileMenuRef.current?.querySelectorAll<HTMLElement>(
                  'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
                );
                if (!focusables || focusables.length === 0) return;

                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement as HTMLElement | null;

                if (event.shiftKey && active === first) {
                  event.preventDefault();
                  last.focus();
                  return;
                }

                if (!event.shiftKey && active === last) {
                  event.preventDefault();
                  first.focus();
                }
              }}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  <span className="text-lg font-bold text-natuvia-text">Menu</span>
                  <button
                    ref={mobileCloseButtonRef}
                    onClick={() => setIsOpen(false)}
                    className="p-3 min-w-[44px] min-h-[44px] -mr-3 rounded-lg hover:bg-natuvia-text/5 transition-colors"
                    aria-label="Close menu"
                  >
                    <svg
                      className="w-6 h-6 text-natuvia-text"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
                  {links.map(link => (
                    <Link
                      key={link.path}
                      to={makePath(link.path)}
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-bold py-3 px-4 -mx-4 rounded-xl transition-colors ${
                        isActive(link.path)
                          ? 'text-natuvia-cta bg-natuvia-cta/5'
                          : 'text-natuvia-text hover:bg-natuvia-text/5'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>

                <div className="mt-6 pt-6 border-t border-natuvia-text/10 space-y-6">
                  <div>
                    <span className="text-xs font-bold text-natuvia-text/40 uppercase tracking-widest block mb-3">
                      {t.currency}
                    </span>
                    <div className="flex gap-2">
                      {CURRENCY_OPTS.map(c => (
                        <button
                          key={c.code}
                          onClick={() => handleCurrencyChange(c.code)}
                          className={`flex-1 text-sm font-bold py-3 px-3 min-h-[44px] rounded-xl transition-all ${
                            currency === c.code
                              ? 'bg-natuvia-ctaDark text-white shadow-md'
                              : 'bg-natuvia-text/5 text-natuvia-text hover:bg-natuvia-text/10'
                          }`}
                        >
                          {c.code}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className="text-xs font-bold text-natuvia-text/40 uppercase tracking-widest block mb-3">
                      {t.language}
                    </span>
                    <div className="flex gap-2">
                      {Object.entries(LOCALES).map(([key, data]) => (
                        <button
                          key={key}
                          onClick={() => setLanguage(key as any)}
                          className={`flex-1 text-sm font-bold py-3 px-3 min-h-[44px] rounded-xl transition-all ${
                            language === key
                              ? 'bg-natuvia-ctaDark text-white shadow-md'
                              : 'bg-natuvia-text/5 text-natuvia-text hover:bg-natuvia-text/10'
                          }`}
                        >
                          {data.pathPrefix.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 space-y-2">
                    {user ? (
                      <>
                        <Link
                          to={makePath(AppRoutes.ACCOUNT)}
                          onClick={() => setIsOpen(false)}
                          className="block text-lg font-bold text-natuvia-text py-3 px-4 -mx-4 rounded-xl hover:bg-natuvia-text/5 transition-colors"
                        >
                          {t.account}
                        </Link>
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsOpen(false);
                          }}
                          className="w-full text-left text-lg font-bold text-natuvia-text/60 py-3 px-4 -mx-4 rounded-xl hover:bg-natuvia-text/5 transition-colors"
                        >
                          {t.logout}
                        </button>
                      </>
                    ) : (
                      <Link
                        to={makePath(AppRoutes.LOGIN)}
                        onClick={() => setIsOpen(false)}
                        className="block text-lg font-bold text-natuvia-text py-3 px-4 -mx-4 rounded-xl hover:bg-natuvia-text/5 transition-colors"
                      >
                        {t.login}
                      </Link>
                    )}

                    <button
                      onClick={() => {
                        openCart();
                        setIsOpen(false);
                      }}
                      className="w-full text-left text-lg font-bold text-natuvia-text py-3 px-4 -mx-4 rounded-xl hover:bg-natuvia-text/5 transition-colors flex items-center justify-between"
                    >
                      <span>{content.cart?.title || 'Cart'}</span>
                      {totalQty > 0 && (
                        <span className="bg-natuvia-cta text-white text-sm font-bold rounded-full px-3 py-1">
                          {totalQty}
                        </span>
                      )}
                    </button>
                  </div>

                  <Button
                    variant="primary"
                    to={makePath(AppRoutes.SHOP)}
                    fullWidth
                    size="lg"
                    onClick={() => setIsOpen(false)}
                  >
                    {content.nav.shop}
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
